import { Button } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import type { NotificationInfo } from "@zhihaoscm/entity";
import { NOTIFICATION_TYPE } from "@zhihaoscm/meta";
import { Breadcrumb, ErrorState } from "@/components/common";
import { notificationService } from "@/api/notificationService";
import { fileService } from "@/api/fileService";

/** 将富文本中的相对链接补齐协议，并剔除可执行脚本节点。 */
export function normalizeNotificationHtml(html: string): string {
    const documentValue = new DOMParser().parseFromString(html, "text/html");
    documentValue.querySelectorAll("script").forEach(node => node.remove());
    documentValue.querySelectorAll("a").forEach(node => {
        const href = node.getAttribute("href");
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
        if (href && !/^(https?:|mailto:|tel:|#)/i.test(href)) node.setAttribute("href", `http://${href}`);
    });
    return documentValue.body.innerHTML;
}

/** 复刻旧 editorManage：清除文件 ID 头，并将带 #id 的历史媒体地址刷新为文件服务真实路径。 */
export async function resolveNotificationHtml(html: string): Promise<string> {
    const content = html.replace(/^==ids=(.*?)\n/, "");
    const sources = Array.from(content.matchAll(/<(?:img|video|source)\s+[^>]*src=["']([^"']+)["']/g)).map(
        match => match[1]
    );
    const media = Array.from(new Set(sources))
        .map(src => ({ src, id: Number(src.split("#")[1]) }))
        .filter(item => item.id);
    if (!media.length) return normalizeNotificationHtml(content);
    const files = await fileService.getFileByIds(media.map(item => item.id));
    let resolved = content;
    media.forEach((item, index) => {
        const path = files[index]?.path;
        if (path) resolved = resolved.split(item.src).join(`${path}#${item.id}`);
    });
    return normalizeNotificationHtml(resolved);
}

/** 通知详情保留标题、分类、发布日期、富文本媒体和独立窗口返回行为。 */
export function NotificationDetailPage() {
    const id = Number(new URLSearchParams(location.search).get("id"));
    const [info, setInfo] = useState<NotificationInfo>();
    const [content, setContent] = useState("");
    const [error, setError] = useState<string>();
    const [loading, setLoading] = useState(true);
    /** 缺失或非法 ID 不发请求，直接显示可理解的失败状态。 */
    const load = async () => {
        if (!Number.isFinite(id) || id <= 0) {
            setError("缺少有效的通知 ID");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(undefined);
        try {
            const detail = await notificationService.getDetail(id);
            setInfo(detail);
            setContent(await resolveNotificationHtml(detail.notification.content ?? ""));
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : "通知加载失败");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    // 旧 Vue 通知详情始终保留静默请求，不额外展示页面级加载框。
    if (loading) return null;
    if (error || !info?.notification) return <ErrorState message={error ?? "通知不存在"} onRetry={() => void load()} />;
    const notification = info.notification;
    return (
        <section className="notification-detail-page">
            <div className="notification-detail-head section-card">
                <Breadcrumb
                    routes={[
                        { path: "/userMessage", name: "我的消息" },
                        { path: "/userMessage?type=notify", name: "通知" },
                        { path: "/userMessage/notification", name: "通知详情" }
                    ]}
                />
                <h1>通知详情</h1>
            </div>
            <div className="notification-detail-content section-card">
                <div className="notification-detail-title">
                    <h2>{notification.title}</h2>
                    <div className="notification-detail-page__meta">
                        <span>
                            {notification.classify === NOTIFICATION_TYPE.OPERATIONAL_NOTIFICATION.code
                                ? "运营通知"
                                : "系统通知"}
                        </span>
                        <span>发布日期 {moment(notification.publishTime).format("YYYY年MM月DD日")}</span>
                    </div>
                </div>
                <article
                    className="notification-content"
                    onClick={event => {
                        const target = event.target as HTMLElement;
                        if (target.tagName === "IMG") window.open((target as HTMLImageElement).src, "_blank");
                    }}
                    dangerouslySetInnerHTML={{ __html: content }}
                />
            </div>
            <Button className="notification-go-back" type="primary" onClick={() => window.close()}>
                返回
            </Button>
        </section>
    );
}
